import { PropsWithChildren, ReactElement, useMemo, useCallback, useEffect } from 'react'
import styles from 'components/features/future/FuturePanePreset/FuturePanePreset.styl'
import { FutureLayerSlug, FutureLayer, FutureLayerType } from 'components/features/future/types'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { memberBirthDateSelector } from 'selectors/globalSelectors'
import { useForm } from 'react-hook-form'
import { ObjectSchema } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { postFutureLayerActionCreator, patchFutureLayerActionCreator, resetPostFutureLayerErrorActionCreator } from 'components/features/future/actions'
import { modifyFloatingPaneActionCreator, killAllFloatingPanesActionCreator } from 'actions/floatingPanesActions'
import { FloatingPaneLayout } from 'components/features/pfm/FloatingPaneLayout/FloatingPaneLayout'
import { FloatingPaneHeader } from 'components/features/pfm/FloatingPaneHeader/FloatingPaneHeader'
import { capitalizeFirstLetter } from 'helpers/formatHelpers'
import { FloatingPaneFooter } from 'components/features/pfm/FloatingPaneFooter/FloatingPaneFooter'
import moment from 'moment'
import { FuturePaneOverlay } from 'components/features/future/FuturePaneOverlay/FuturePaneOverlay'
import { LayerNameInput } from 'components/features/future/LayerNameInput/LayerNameInput'
import { Theme } from 'interfaces/component'
import { getProgressBarWidth } from 'helpers/formHelpers'
import Button from 'components/features/pfm/Button/Button'
import { sleep } from 'helpers/utilsHelpers'
import { FormPreset } from 'components/atoms/inputs/FormPreset/FormPreset'


type FuturePanePresetProps<
  Slug extends FutureLayerSlug,
  LayerType = Extract<FutureLayer, { slug: Slug }>,
  LayerFields extends Object = Extract<FutureLayer, { slug: Slug }>
> = {
  slug: Slug
  layer: LayerType | null
  type: FutureLayerType
  name: string
  icon: string
  schemaCreator: (birthDate: string) => ObjectSchema<LayerFields>
  onCancel: () => void
}

export const FuturePanePreset =
  <Slug extends FutureLayerSlug>(props: PropsWithChildren<FuturePanePresetProps<Slug>>): ReactElement | null => {

    const birthDate = useStoreSelector(memberBirthDateSelector)
    if (!birthDate) {
      return null
    }

    return (
      <FuturePanePresetInner<Slug>
        { ...props }
        birthDate={ birthDate }
      />
    )
  }

type FuturePanePresetInnerProps<Slug extends FutureLayerSlug> =
  & FuturePanePresetProps<Slug>
  & {
    birthDate: string
  }

const FuturePanePresetInner =
  <Slug extends FutureLayerSlug>(props: PropsWithChildren<FuturePanePresetInnerProps<Slug>>): ReactElement => {

    const { schemaCreator, layer, children, onCancel, type, name, icon, slug, birthDate } = props
    const { uid } = layer || { uid: '' }
    const inLayerEditMode = !!uid
    const resolver = useMemo(() => yupResolver(schemaCreator(birthDate)), [birthDate, schemaCreator])
    const formContext = useForm<FutureLayer>({
      defaultValues: {
        status: 'enabled',
        when: {
          slug: 'at_month',
          month: moment().add(1, 'month').format('YYYY-MM'),
        },
        name,
        slug,
        ...layer,
      },
      resolver,
      mode: 'all',
    })

    const { handleSubmit, register, watch, reset, formState: { errors, isSubmitting, isValid }} = formContext
    const dispatch = useStoreDispatch()
    const status = watch('status') as FutureLayer['status']

    const handleSeeAll = useCallback(() => {
      dispatch(killAllFloatingPanesActionCreator())
      if (uid) {
        dispatch(modifyFloatingPaneActionCreator('future', { mode: 'member-layers' }))
      } else {
        dispatch(modifyFloatingPaneActionCreator('future', { mode: 'all-layers' }))
      }
    }, [dispatch, uid])

    const handleDismissErrors = useCallback(() => {
      dispatch(resetPostFutureLayerErrorActionCreator())
      reset(undefined, { keepValues: true })
    }, [dispatch, reset])

    const handleSuccessSubmit = useMemo(() => handleSubmit(async (data) => {
      await sleep(500)
      handleDismissErrors()
      if (!uid) {
        dispatch(postFutureLayerActionCreator.request(data))
      } else {
        dispatch(patchFutureLayerActionCreator.request({
          ...data,
          uid,
        }))
      }
    }), [handleSubmit, handleDismissErrors, uid, dispatch])

    useEffect(() => {
      return () => {
        handleDismissErrors()
        dispatch(resetPostFutureLayerErrorActionCreator())
      }
    }, [dispatch, handleDismissErrors])

    const nameError = errors.name && errors.name.message
    const nameInput = useMemo(() => (
      <LayerNameInput error={ nameError } />
    ), [nameError])
    const isLoading = useStoreSelector((state) => state.data.advisors.advisorsRequest.isLoading)

    const headerChild = useMemo(() => (
      <FloatingPaneHeader
        progressBarPercent={ inLayerEditMode ? undefined : getProgressBarWidth(formContext.formState, isValid ? 75 : 50) }
        title={ nameInput }
        iconSlug={ icon }
        superTitle={ type.toUpperCase() }
        theme={ type }
        withCircledIcon
        centered
        onAction={ handleSeeAll }
        actionText='See All'
        isLoading={ isLoading || isSubmitting }
      />
    ), [inLayerEditMode, formContext.formState, isValid, nameInput, icon, type, handleSeeAll, isLoading, isSubmitting])

    const footerChild = useMemo(() => {
      const isComplete = status === 'completed' || status === 'partially_completed'

      const actionName =
      isComplete ? undefined :
        (inLayerEditMode ? 'Update ' : 'Add ') + capitalizeFirstLetter(type)

      const actionTheme: Theme | undefined = status !== 'past' ? undefined : 'lightBlue'

      const cancelName = isComplete ? 'Close' : undefined
      const cancelTheme: Theme | undefined = !isComplete ? undefined : 'lightBlue'

      const primaryButtonChild = !!actionName && (
        <Button
          text={ actionName }
          theme={ actionTheme }
          onClick={ handleSuccessSubmit }
          isSubmitting={ isLoading || isSubmitting }
        />
      )

      return (
        <FloatingPaneFooter
          isLoading={ isLoading || isSubmitting }
          primaryButton={ primaryButtonChild }
          cancelName={ cancelName }
          cancelTheme={ cancelTheme }
          onCancel={ onCancel }
        />
      )
    }, [status, inLayerEditMode, type, isLoading, isSubmitting, handleSuccessSubmit, onCancel])

    const errorChild = useMemo(() => (
      <FuturePaneOverlay type={ type } onDismiss={ handleDismissErrors } />
    ), [type, handleDismissErrors])

    return (
      <FormPreset formContext={ formContext } onSubmit={ handleSuccessSubmit } debug>
        <FloatingPaneLayout
          type='future'
          header={ headerChild }
          footer={ footerChild }
          bodyClassName={ styles.FuturePanePreset }
          notification={ errorChild }
          withStaticHeader
          onBack={ handleSeeAll }
          onClose={ onCancel }
        >
          { inLayerEditMode && (
            <input type='hidden' { ...register('uid') } value={ uid } />
          )}
          <input type='hidden' { ...register('slug') } value={ slug } />
          { children }
          <br />
        </FloatingPaneLayout>
      </FormPreset>

    )
  }
